import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Receipt,
  ListPlus,
  Download,
  Plus,
  Loader2,
  Trash2,
  Repeat,
  ArrowDownRight,
  ArrowUpRight,
  Wallet,
  X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import InvoicesManager from "@/components/admin/InvoicesManager";

const SUBTABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "expenses", label: "Expenses", icon: ListPlus },
  { id: "invoices", label: "Invoices", icon: Receipt },
];

// Compute current FY start year (Australian FY, Jul→Jun) in local time.
function currentFyStart() {
  const d = new Date();
  return d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
}
function fyLabel(y) { return `${y}/${String(y + 1).slice(-2)}`; }

export default function FinanceWorkspace() {
  const [tab, setTab] = useState("overview");
  const [fy, setFy] = useState(currentFyStart());

  const fyOptions = useMemo(() => {
    const cur = currentFyStart();
    return [cur - 2, cur - 1, cur, cur + 1];
  }, []);

  return (
    <div data-testid="finance-workspace">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Finance</div>
          <h2 className="font-serif text-3xl text-ink">Income, expenses & invoicing.</h2>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-[0.18em] text-ink-soft">Financial year</label>
          <select
            value={fy}
            onChange={(e) => setFy(Number(e.target.value))}
            data-testid="finance-fy-select"
            className="rounded-full border border-sand bg-cream px-4 py-2 text-sm text-ink focus:outline-none focus:border-navy"
          >
            {fyOptions.map((y) => <option key={y} value={y}>FY {fyLabel(y)}</option>)}
          </select>
        </div>
      </div>

      <div className="flex gap-1 mb-8 border-b border-sand/60 overflow-x-auto">
        {SUBTABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`finance-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                tab === t.id ? "border-navy text-navy" : "border-transparent text-ink-soft hover:text-ink"
              }`}
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <FinanceOverview fy={fy} />}
      {tab === "expenses" && <ExpensesManager fy={fy} />}
      {tab === "invoices" && <InvoicesManager />}
    </div>
  );
}

// ============================================================
// OVERVIEW — headline numbers, monthly stacked bar, category donut
// ============================================================
function FinanceOverview({ fy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/finance/summary?fy=${fy}`);
      setData(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [fy]);

  const download = () => {
    const token = localStorage.getItem("ams_token");
    const url = `${api.defaults.baseURL}/admin/finance/export?fy=${fy}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `finance-fy${fy}-${fy + 1}.csv`;
        a.click();
      })
      .catch(() => toast.error("Could not export CSV"));
  };

  if (loading || !data) {
    return <div className="py-16 flex items-center justify-center text-ink-muted"><Loader2 size={20} className="animate-spin mr-2" /> Loading FY summary…</div>;
  }

  const { totals, series, by_category } = data;
  const maxMonth = Math.max(1, ...series.map((m) => Math.max(m.income, m.expenses)));

  return (
    <div className="space-y-8" data-testid="finance-overview">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Income" value={totals.income} icon={ArrowUpRight} tone="bg-sage/12 text-sage-deep border-sage/40" />
        <StatCard label="Expenses" value={totals.expenses} icon={ArrowDownRight} tone="bg-clay/12 text-clay-deep border-clay/40" />
        <StatCard label="Net" value={totals.net} icon={Wallet} tone={totals.net >= 0 ? "bg-navy/10 text-navy border-navy/40" : "bg-clay/12 text-clay-deep border-clay/40"} />
        <div className="rounded-2xl border border-sand/70 bg-cream-soft p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-1">Activity</div>
          <div className="font-serif text-3xl text-ink tabular-nums">{totals.invoices_paid + totals.expense_entries}</div>
          <div className="text-xs text-ink-soft mt-1">{totals.invoices_paid} invoices · {totals.expense_entries} expenses</div>
          <button
            type="button"
            onClick={download}
            data-testid="finance-export-btn"
            className="mt-3 inline-flex items-center gap-2 text-xs rounded-full bg-navy text-cream px-4 py-1.5 hover:bg-navy-deep transition"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-sand/70 bg-cream-soft p-6" data-testid="finance-monthly-chart">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-1">Monthly · income vs expenses</div>
            <div className="font-serif text-2xl text-ink">FY {fyLabel(fy)}</div>
          </div>
          <div className="flex items-center gap-4 text-[11px] text-ink-soft">
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-sage-deep" /> Income</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-clay-deep" /> Expenses</span>
          </div>
        </div>
        <div className="grid grid-cols-12 gap-2 h-40 items-end">
          {series.map((m) => (
            <div key={m.month} className="flex flex-col gap-1 items-center justify-end" title={`${m.month} · Income $${m.income} · Expenses $${m.expenses} · Net $${m.net}`}>
              <div className="w-full flex gap-0.5 items-end h-full">
                <div className="flex-1 rounded-t bg-sage-deep transition-all" style={{ height: `${(m.income / maxMonth) * 100}%`, minHeight: m.income > 0 ? "3px" : "0" }} />
                <div className="flex-1 rounded-t bg-clay-deep transition-all" style={{ height: `${(m.expenses / maxMonth) * 100}%`, minHeight: m.expenses > 0 ? "3px" : "0" }} />
              </div>
              <div className="text-[9px] text-ink-soft tabular-nums">{m.month.slice(-2)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-3xl border border-sand/70 bg-cream-soft p-6" data-testid="finance-categories">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-1">Expenses by category</div>
        <div className="font-serif text-2xl text-ink mb-5">Where the money is going.</div>
        {by_category.length === 0 ? (
          <div className="text-sm text-ink-soft italic">No expenses recorded for FY {fyLabel(fy)} yet.</div>
        ) : (
          <div className="space-y-2">
            {by_category.map((c) => {
              const pct = totals.expenses > 0 ? (c.total / totals.expenses) * 100 : 0;
              return (
                <div key={c.slug} className="grid grid-cols-12 gap-3 items-center text-sm">
                  <div className="col-span-4 text-ink truncate">{c.name}</div>
                  <div className="col-span-6">
                    <div className="h-2 bg-sand/50 rounded-full overflow-hidden">
                      <div className="h-full bg-clay-deep rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="col-span-2 text-right tabular-nums text-ink-muted">${c.total.toFixed(2)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  return (
    <div className={`rounded-2xl border p-5 ${tone}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="text-[11px] uppercase tracking-[0.2em] opacity-80">{label}</div>
        <Icon size={16} />
      </div>
      <div className="font-serif text-3xl tabular-nums mt-1">${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
    </div>
  );
}

// ============================================================
// EXPENSES — list + add form
// ============================================================
function ExpensesManager({ fy }) {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [e, c] = await Promise.all([
        api.get(`/admin/expenses?fy=${fy}${filter ? `&category=${filter}` : ""}`),
        api.get("/admin/expense-categories"),
      ]);
      setExpenses(e.data);
      setCategories(c.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [fy, filter]);

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  const remove = async (exp) => {
    const isSeries = !!exp.series_id;
    let scope = "one";
    if (isSeries) {
      const yes = window.confirm(`This is a ${exp.frequency} recurring expense. Delete this occurrence only? (Cancel to delete the whole series from this date forward)`);
      scope = yes ? "one" : "series";
    } else if (!window.confirm(`Delete expense "${exp.description}"?`)) return;
    try {
      await api.delete(`/admin/expenses/${exp.id}?scope=${scope}`);
      toast.success(scope === "series" ? "Series deleted." : "Expense deleted.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="expenses-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="expenses-category-filter"
            className="rounded-full border border-sand bg-cream px-4 py-2 text-sm text-ink focus:outline-none focus:border-navy"
          >
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <span className="text-xs text-ink-soft">{expenses.length} entries</span>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          data-testid="expenses-add-btn"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-4 py-2 text-sm font-medium hover:bg-navy-deep transition"
        >
          <Plus size={14} /> Add expense
        </button>
      </div>

      {showForm && (
        <ExpenseForm
          categories={categories}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); refresh(); }}
        />
      )}

      {loading ? (
        <div className="py-16 flex items-center justify-center text-ink-muted"><Loader2 size={20} className="animate-spin mr-2" /> Loading…</div>
      ) : expenses.length === 0 ? (
        <div className="text-center py-16 text-ink-soft italic font-serif text-lg">No expenses in this view yet.</div>
      ) : (
        <div className="rounded-2xl border border-sand/70 bg-cream-soft overflow-hidden" data-testid="expenses-table">
          <table className="w-full text-sm">
            <thead className="bg-sand/30 text-ink-soft text-[11px] uppercase tracking-[0.16em]">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Date</th>
                <th className="text-left px-4 py-2.5 font-medium">Category</th>
                <th className="text-left px-4 py-2.5 font-medium">Description</th>
                <th className="text-right px-4 py-2.5 font-medium">Amount</th>
                <th className="text-center px-4 py-2.5 font-medium">Recurring</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => (
                <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-t border-sand/40">
                  <td className="px-4 py-2.5 tabular-nums text-ink">{format(new Date(e.date), "d MMM yyyy")}</td>
                  <td className="px-4 py-2.5 text-ink-muted">{catName(e.category)}</td>
                  <td className="px-4 py-2.5 text-ink">
                    {e.description}
                    {e.notes && <div className="text-[12px] text-ink-soft mt-0.5">{e.notes}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink">${e.amount.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-center">
                    {e.frequency && (
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-navy/10 text-navy">
                        <Repeat size={10} /> {e.frequency}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => remove(e)} data-testid={`expense-delete-${e.id}`} className="text-ink-soft hover:text-clay-deep p-1.5 rounded-full hover:bg-clay/10 transition">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ExpenseForm({ categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: categories[0]?.slug || "",
    description: "",
    amount: "",
    notes: "",
    frequency: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.description.trim() || !form.amount || Number(form.amount) <= 0) {
      toast.error("Enter a description and a positive amount.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        category: form.category,
        description: form.description.trim(),
        amount: Number(form.amount),
        notes: form.notes.trim(),
      };
      if (form.frequency) payload.frequency = form.frequency;
      const r = await api.post("/admin/expenses", payload);
      const created = r.data?.count ? `Created ${r.data.count} occurrences.` : "Expense added.";
      toast.success(created);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const inputCls = "w-full rounded-xl border border-sand/70 bg-cream px-4 py-2.5 text-sm text-ink placeholder:text-ink-soft focus:outline-none focus:border-navy";

  return (
    <div className="rounded-2xl border border-navy/30 bg-cream p-5" data-testid="expense-form">
      <div className="flex items-center justify-between mb-4">
        <div className="font-serif text-xl text-ink">New expense</div>
        <button type="button" onClick={onClose} className="h-8 w-8 rounded-full text-ink-soft hover:bg-sand/40 flex items-center justify-center">
          <X size={16} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.16em] text-ink-soft mb-1">Date</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-form-date" className={inputCls} />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.16em] text-ink-soft mb-1">Category</label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="expense-form-category" className={inputCls}>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[11px] uppercase tracking-[0.16em] text-ink-soft mb-1">Description</label>
          <input placeholder="e.g. Monthly supervision session with Dr X" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-form-description" className={inputCls} />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.16em] text-ink-soft mb-1">Amount (AUD)</label>
          <input type="number" step="0.01" placeholder="0.00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-form-amount" className={inputCls} />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.16em] text-ink-soft mb-1">Recurring</label>
          <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="expense-form-frequency" className={inputCls}>
            <option value="">One-off</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="annually">Annually</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[11px] uppercase tracking-[0.16em] text-ink-soft mb-1">Notes (optional)</label>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="expense-form-notes" className={`${inputCls} resize-none`} />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-end gap-3">
        <button type="button" onClick={onClose} className="text-sm text-ink-soft hover:text-ink">Cancel</button>
        <button
          type="button"
          disabled={saving}
          onClick={submit}
          data-testid="expense-form-save"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium disabled:opacity-50"
        >
          {saving ? "Saving…" : (form.frequency ? "Save & generate series" : "Save expense")}
        </button>
      </div>
      {form.frequency && (
        <p className="text-[11px] text-ink-soft mt-3">
          A {form.frequency} recurring series will be generated up to the end of the next financial year.
        </p>
      )}
    </div>
  );
}
